import React from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { Alert } from '../../../UI/Common';
import { withTranslation } from 'react-i18next';
import exportIcon from './download-icon.svg';
import helper from '../../../../utils/helper';

am4core.useTheme(am4themes_animated);
const dateFormat = 'MM/dd/yyyy h:mm a';

class TrendChartsPage extends React.Component {
  constructor(props) {
    super(props);
    this.chart = null;
  }
  componentDidMount() {
    const { data } = this.props;
    this.chart = am4core.create('chartdiv', am4charts.XYChart);
    this.drawChart(data, this.props.chartMin, this.props.chartMax);
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.dispose();
    }
  }

  drawChart = (data = [], chartMin, chartMax) => {
    const { chart } = this;
    const { metaData } = this.props;
    chart.data = data;
    chart.dateFormatter.dateFormat = dateFormat;
    let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.groupData = true;
    dateAxis.renderer.minGridDistance = 20;
    dateAxis.renderer.grid.template.location = 0.5;
    dateAxis.startLocation = 0.5;
    dateAxis.endLocation = 0.5;

    // Setting up label rotation
    dateAxis.renderer.labels.template.rotation = 90;
    dateAxis.renderer.labels.template.verticalCenter = 'middle';
    dateAxis.renderer.labels.template.horizontalCenter = 'left';
    // dateAxis.groupInterval = { timeUnit: 'second', count: 60 };
    dateAxis.dateFormats.setKey('minute', dateFormat);
    dateAxis.periodChangeDateFormats.setKey('minute', dateFormat);

    // this hides regular labels close to the start/end
    dateAxis.renderer.minLabelPosition = 0.15;
    dateAxis.renderer.maxLabelPosition = 0.85;
    // add ranges
    var minRange = dateAxis.axisRanges.create();
    // this overrides minLabelPosition/maxLabelPosition so that the range labels would be visible
    minRange.maxPosition = 1;
    minRange.minPosition = 0;
    // minRange.label.horizontalCenter = 'left';
    minRange.label.paddingLeft = 0;

    var maxRange = dateAxis.axisRanges.create();
    // this overrides minLabelPosition/maxLabelPosition so that the range labels would be visible
    maxRange.maxPosition = 1;
    maxRange.minPosition = 0;
    // maxRange.label.horizontalCenter = 'right';
    maxRange.label.paddingRight = 0;

    dateAxis.events.on('startendchanged', () => updateRangeLabels());
    dateAxis.events.on('extremeschanged', () => updateRangeLabels());

    function updateRangeLabels() {
      minRange.value =
        dateAxis.min + dateAxis.start * (dateAxis.max - dateAxis.min);
      minRange.label.text = dateAxis.dateFormatter.format(
        minRange.value,
        dateFormat,
      );
      maxRange.value =
        dateAxis.min + dateAxis.end * (dateAxis.max - dateAxis.min);
      maxRange.label.text = dateAxis.dateFormatter.format(
        maxRange.value,
        dateFormat,
      );
    }

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.tooltip.disabled = true;
    valueAxis.strictMinMax = true;
    if (chartMin !== null) {
      valueAxis.min = chartMin;
    }
    if (chartMax) {
      valueAxis.max = chartMax;
    } else {
      if (this.props.maxDataPoint) {
        valueAxis.max = this.props.maxDataPoint + 1;
      }
    }
    // const range = valueAxis.axisRanges.create();
    // range.value = 8;
    // range.grid.stroke = am4core.color('#ffb0be');
    // range.grid.strokeWidth = 2;
    // range.grid.strokeOpacity = 1;
    // range.label.inside = true;
    // range.label.text = 'Upper Limit';
    // range.label.fill = range.grid.stroke;
    // range.label.align = 'right';
    // range.label.verticalCenter = 'bottom';

    // var range2 = valueAxis.axisRanges.create();
    // range2.value = 3;
    // range2.grid.stroke = am4core.color('#fcf496');
    // range2.grid.strokeWidth = 2;
    // range2.grid.strokeOpacity = 1;
    // range2.label.inside = true;
    // range2.label.text = 'Lower Limit';
    // range2.label.fill = range2.grid.stroke;
    // range2.label.align = 'right';
    // range2.label.verticalCenter = 'bottom';

    let scrollbarX = new am4charts.XYChartScrollbar();
    metaData.map(({ name, units, lowAlarm, hiAlarm }, index) => {
      this.createSeries(name, name, scrollbarX, units);
      if (lowAlarm) {
        const range2 = valueAxis.axisRanges.create();
        range2.value = lowAlarm;
        range2.grid.stroke = am4core.color('#fcf496');
        range2.grid.strokeWidth = 2;
        range2.grid.strokeOpacity = 1;
        range2.label.inside = true;
        range2.label.text = `Low Alarm ${name}`;
        range2.label.fill = range2.grid.stroke;
        range2.label.align = 'right';
        range2.label.verticalCenter = 'bottom';
      }
      if (hiAlarm) {
        const range = valueAxis.axisRanges.create();
        range.value = hiAlarm;
        range.grid.stroke = am4core.color('#ffb0be');
        range.grid.strokeWidth = 2;
        range.grid.strokeOpacity = 1;
        range.label.inside = true;
        range.label.text = `High Alarm ${name}`;
        range.label.fill = range.grid.stroke;
        range.label.align = 'right';
        range.label.verticalCenter = 'bottom';
      }
    });

    chart.scrollbarX = scrollbarX;
    let scrollAxis = chart.scrollbarX.scrollbarChart.xAxes.getIndex(0);
    if (scrollAxis) {
      scrollAxis.renderer.labels.template.disabled = true;
      scrollAxis.renderer.grid.template.disabled = true;
    }
    if (helper.isMobile()) {
      chart.scrollbarX.parent = chart.bottomAxesContainer;
    }
    chart.legend = new am4charts.Legend();
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.maxTooltipDistance = 0;
    chart.exporting.menu = new am4core.ExportMenu();
    chart.exporting.menu.items[0].icon = exportIcon;
    chart.exporting.dateFields.push('date');
    chart.exporting.dateFormat = 'MM/dd/yyyy h:mm:ss a';
  };

  createSeries = (field, name, scrollbarX, units) => {
    const { chart } = this;
    const series = chart.series.push(new am4charts.LineSeries());
    series.dataFields.valueY = field;
    series.dataFields.dateX = 'date';
    series.name = name;
    series.tooltipText =
      '{dateX.formatDate("MM/dd/yyyy h:mm a")}: [b]{valueY}[/]';
    series.strokeWidth = 2;

    // Set up tooltip
    series.adapter.add('tooltipText', (ev) => {
      let text = '[bold]{dateX.formatDate("MM/dd/yyyy h:mm a")}[/]\n';
      chart.series.each(function (item) {
        text +=
          '[' +
          item.stroke.hex +
          ']●[/] ' +
          item.name +
          ': {' +
          item.dataFields.valueY +
          '}' +
          ' ' +
          units +
          '\n';
      });
      return text;
    });

    series.tooltip.getFillFromObject = false;
    series.tooltip.background.fill = am4core.color('#fff');
    series.tooltip.label.fill = am4core.color('#00');
    series.smoothing = 'monotoneX';

    const bullet = series.bullets.push(new am4charts.CircleBullet());
    bullet.circle.stroke = am4core.color('#fff');
    bullet.circle.strokeWidth = 2;

    scrollbarX.series.push(series);
    return series;
  };

  render() {
    const { t } = this.props;
    return (
      <div className="content-inner">
        <Alert.Error text={this.props.errors} />
        {!this.props.data.length && (
          <h1>No data for the time period selected</h1>
        )}
        <div id="chartdiv" style={{ width: '100%', height: '100vh' }}></div>
      </div>
    );
  }
}

export default withTranslation('app')(TrendChartsPage);
